import {formatDateString, stripTimezone} from './date-formatting'

/**
 * A single custom issue field value, normalized into a framework-agnostic shape
 * that both the Relay row (`IssueFieldPills`) and the REST dashboard row
 * (`IssueListItem`) map into before formatting. `dataType` discriminates how the
 * value is carried:
 * - `text` / `single_select` / `date` → `value`
 * - `number` → `numberValue`
 * - `multi_select` → `options` (selected option names)
 *
 * This module intentionally has NO GraphQL/Relay dependency so REST/TanStack
 * surfaces can reuse the exact same pill formatting as the Relay row.
 */
export interface IssueFieldValue {
  fieldName: string
  dataType: 'text' | 'single_select' | 'multi_select' | 'date' | 'number'
  value?: string | null
  numberValue?: number | null
  options?: string[] | null
}

/**
 * A single rendered pill: the user-facing `displayValue` plus the `queryValue`
 * used to build a `field.<name>:` search filter when the pill is clicked.
 */
export interface IssueFieldBadge {
  fieldName: string
  displayValue: string
  queryValue: string
}

/**
 * Turns one normalized issue field value into zero or more pill badges. Kept
 * framework-agnostic so Relay and REST rows format fields identically:
 * - number: stringified value
 * - text / single-select: value used as both display and query value
 * - multi-select: fans out into one badge per selected option
 * - date: display uses the timezone-stripped formatted date; query uses the raw
 *   ISO calendar date (`YYYY-MM-DD`) so search matches backend-stored values
 */
export function getIssueFieldBadges(fieldValue: IssueFieldValue): IssueFieldBadge[] {
  const fieldName = fieldValue.fieldName
  if (!fieldName) return []

  switch (fieldValue.dataType) {
    case 'number': {
      if (fieldValue.numberValue == null) return []
      const displayValue = String(fieldValue.numberValue)
      return [{fieldName, displayValue, queryValue: displayValue}]
    }
    case 'single_select':
      return fieldValue.value ? [{fieldName, displayValue: fieldValue.value, queryValue: fieldValue.value}] : []
    case 'multi_select':
      return (fieldValue.options ?? []).flatMap(option =>
        option ? [{fieldName, displayValue: option, queryValue: option}] : [],
      )
    case 'text':
      return fieldValue.value ? [{fieldName, displayValue: fieldValue.value, queryValue: fieldValue.value}] : []
    case 'date': {
      if (!fieldValue.value) return []
      const date = stripTimezone(fieldValue.value)
      if (!date) return []
      return [{fieldName, displayValue: formatDateString(date), queryValue: fieldValue.value.slice(0, 10)}]
    }
    default:
      return []
  }
}

/** Maps a list of normalized field values into a flat list of pill badges. */
export function getIssueFieldBadgesFromValues(fieldValues: IssueFieldValue[]): IssueFieldBadge[] {
  return fieldValues.flatMap(getIssueFieldBadges)
}
